"use client";

import { scaleLinear, scaleTime } from "@visx/scale";
import { Bar } from "@visx/shape";
import { extent, max, min } from "d3-array";
import React, { useMemo } from "react";
import type { BaseDataPoint } from "@/utils/charts";
import type { CellComponentProps } from "../DataTable.types";

// Support both data shapes: BaseDataPoint (xValue/yValue) and legacy {x, y}
type MiniBarDatum = (BaseDataPoint & { category?: string }) | { x: Date; y: number; category?: string };

export interface MiniBarCellProps extends CellComponentProps {
  data?: MiniBarDatum[]; // Optional for direct usage
  color?: string;
  categoryColors?: Record<string, string>;
  categoryLabels?: Record<string, string>;
  width?: number;
  height?: number;
}

/**
 * MiniBarCell
 *
 * Mini vertical bar chart for table cells.
 * Uses the same BaseDataPoint[] data structure as full BarSeries components.
 * Supports category-based coloring for conditional visualization.
 */
export const MiniBarCell = React.memo<MiniBarCellProps>(
  ({ value, data: dataProp, color = "var(--color-viz-default)", categoryColors, context, width, height = 40 }) => {
    // Use value from DataTable accessor, fall back to data prop
    const data = ((value as MiniBarDatum[]) || dataProp || []) as MiniBarDatum[];

    // Helper functions to handle both data shapes
    const getXValue = (point: MiniBarDatum) => ("xValue" in point ? point.xValue : point.x);
    const getYValue = (point: MiniBarDatum) => ("yValue" in point ? point.yValue : point.y);

    const chartWidth = width || 100;
    const chartHeight = height;

    // Create scales
    const { xScale, yScale, barWidth } = useMemo(() => {
      if (data.length === 0) {
        return {
          xScale: scaleTime({
            range: [0, chartWidth],
            domain: [new Date(), new Date()],
          }),
          yScale: scaleLinear({ range: [chartHeight, 0], domain: [0, 1] }),
          barWidth: 2,
        };
      }

      const dateExtent = extent(data, (d) => getXValue(d)) as [Date, Date];
      const yMin = min(data, (d) => getYValue(d)) ?? 0;
      const yMax = max(data, (d) => getYValue(d)) ?? 0;

      // Y scale starts at 0 for bars (or lowest value if negative)
      const yMinValue = yMin < 0 ? yMin : 0;

      const xScaleCalc = scaleTime({
        range: [0, chartWidth],
        domain: dateExtent,
      });

      const yScaleCalc = scaleLinear({
        range: [chartHeight, 0],
        domain: [yMinValue, yMax * 1.05], // 5% padding on top
      });

      // Calculate bar width
      const calculatedBarWidth = data.length > 1 ? (chartWidth / data.length) * 0.8 : 8;

      return {
        xScale: xScaleCalc,
        yScale: yScaleCalc,
        barWidth: Math.max(2, calculatedBarWidth), // Minimum 2px width
      };
    }, [data, chartWidth, chartHeight]);

    // Loading state
    if (context.isLoading) {
      return (
        <div className="flex items-center gap-0.5" style={{ width: chartWidth, height: chartHeight }}>
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="flex-1 animate-pulse bg-border-default rounded" />
          ))}
        </div>
      );
    }

    // Empty state
    if (!data || data.length === 0) {
      return (
        <div
          className="flex items-center justify-center text-text-secondary text-xs"
          style={{ width: chartWidth, height: chartHeight }}
        >
          No data
        </div>
      );
    }

    const zeroY = yScale(0);

    return (
      <svg width={chartWidth} height={chartHeight}>
        {data.map((d, i) => {
          const datumY = getYValue(d);
          const x = xScale(getXValue(d));
          const y = yScale(datumY);
          const barHeight = Math.abs(zeroY - y);

          // Get color based on category if available
          const barColor = d.category && categoryColors?.[d.category] ? categoryColors[d.category] : color;

          return (
            <Bar
              key={`bar-${i}`}
              x={x - barWidth / 2}
              y={datumY >= 0 ? y : zeroY}
              width={barWidth}
              height={barHeight}
              fill={barColor}
              rx={1}
            />
          );
        })}
      </svg>
    );
  }
);

MiniBarCell.displayName = "MiniBarCell";
